import {CloseButton, Combobox, InputBase, type InputBaseProps, useCombobox} from '@mantine/core'
import {type FocusEvent, type KeyboardEvent, useState} from 'react'

import {formatJobTime, parseApiTime} from './job-schedule-format'
import {JOB_TIME_PICKER_PRESETS} from './job-time-picker-presets'

type JobTimePickerProps = Omit<InputBaseProps, 'value' | 'onChange' | 'defaultValue'> & {
    value: string | null
    onChange: (value: string | null) => void
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void
    placeholder?: string
    'aria-label'?: string
}

const TYPED_TIME_PATTERN = /^(\d{1,2})(?::?(\d{2}))?\s*(am?|pm?)?$/

/**
 * Parse a typed time ("1130pm", "11:30 pm", "2330", "4a") to `HH:mm`. Returns null when empty
 * and undefined when invalid.
 */
export function parseTypedTime(text: string): string | null | undefined {
    const cleaned = text.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ')
    if (cleaned === '') {
        return null
    }
    const match = TYPED_TIME_PATTERN.exec(cleaned)
    if (!match) {
        return undefined
    }

    let hours = Number(match[1])
    const minutes = match[2] ? Number(match[2]) : 0
    const period = match[3]?.[0]
    if (minutes > 59) {
        return undefined
    }
    if (period) {
        if (hours < 1 || hours > 12) {
            return undefined
        }

        hours = (hours % 12) + (period === 'p' ? 12 : 0)
    } else if (hours > 23) {
        return undefined
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function formatTimeInputLabel(value: string | null): string {
    const normalized = parseApiTime(value)
    return normalized ? formatJobTime(normalized) : ''
}

export function JobTimePicker({
    value,
    onChange,
    onBlur,
    error,
    disabled,
    placeholder = 'e.g. 7:30 AM',
    ...props
}: JobTimePickerProps) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })
    const [draft, setDraft] = useState<string | null>(null)
    const [invalid, setInvalid] = useState(false)

    const typed = draft != null ? parseTypedTime(draft) : undefined

    function commit(next: string | null) {
        onChange(next)
        setDraft(null)
        setInvalid(false)
        combobox.closeDropdown()
    }

    function commitDraft() {
        if (draft == null) {
            return
        }

        const parsed = parseTypedTime(draft)
        if (parsed === undefined) {
            setInvalid(true)
            return
        }
        commit(parsed)
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        // Enter with no highlighted option commits the draft instead of submitting the form
        if (event.key === 'Enter' && combobox.getSelectedOptionIndex() === -1) {
            event.preventDefault()
            commitDraft()
        }
    }

    const hasValue = value != null && !disabled

    return (
        <Combobox store={combobox} disabled={disabled} onOptionSubmit={(option) => commit(option)}>
            <Combobox.Target>
                <InputBase
                    {...props}
                    disabled={disabled}
                    placeholder={placeholder}
                    value={draft ?? formatTimeInputLabel(value)}
                    error={error ?? (invalid ? 'Enter a valid time, e.g. 7:30 AM' : null)}
                    onChange={(event) => {
                        setDraft(event.currentTarget.value)
                        setInvalid(false)
                        combobox.openDropdown()
                        combobox.resetSelectedOption()
                    }}
                    onClick={() => combobox.openDropdown()}
                    onFocus={() => combobox.openDropdown()}
                    onKeyDown={handleKeyDown}
                    onBlur={(event) => {
                        commitDraft()
                        combobox.closeDropdown()
                        onBlur?.(event)
                    }}
                    rightSection={
                        hasValue ? (
                            <CloseButton
                                size="sm"
                                aria-label="Clear time"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => commit(null)}
                            />
                        ) : undefined
                    }
                    rightSectionPointerEvents={hasValue ? 'all' : 'none'}
                />
            </Combobox.Target>
            <Combobox.Dropdown>
                <Combobox.Options mah={280} style={{overflowY: 'auto'}}>
                    {typed != null && (
                        <Combobox.Group label="Typed">
                            <Combobox.Option value={typed}>{formatJobTime(typed)}</Combobox.Option>
                        </Combobox.Group>
                    )}
                    {JOB_TIME_PICKER_PRESETS.map((group) => (
                        <Combobox.Group key={group.label} label={group.label}>
                            {group.values.map((preset) => (
                                <Combobox.Option
                                    key={preset}
                                    value={preset}
                                    active={preset === parseApiTime(value)}
                                >
                                    {formatJobTime(preset)}
                                </Combobox.Option>
                            ))}
                        </Combobox.Group>
                    ))}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    )
}
